import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        document.body.style.overflow = 'unset';
    };

    return (
        <header
            className="w-full bg-gradient-to-r from-primary via-primary to-primary-dark py-2 md:py-2.5 shadow-md relative z-40"
        >
            <div className="container mx-auto px-4 flex justify-between items-center h-10 md:h-12">
                {/* Logo */}
                <div className="flex items-center h-full">
                    <img
                        src="/backend%20log/footerimg.png"
                        alt="LV Lakshmi Venkatesan Charitable Trust"
                        className="h-8 md:h-10 w-auto cursor-pointer object-contain"
                    />
                </div>

                {/* Mobile Menu Button - Single hamburger button */}
                <div className="md:hidden z-50">
                    <button
                        onClick={toggleMenu}
                        className="text-white focus:outline-none"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                    <Link
                        to="/"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        Home
                    </Link>
                    <Link
                        to="/about"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        About Us
                    </Link>
                    <Link
                        to="/thematic-areas"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        Thematic Areas
                    </Link>

                    {/* Events Dropdown */}
                    <div className="relative group">
                        <div
                            className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide flex items-center cursor-default"
                        >
                            Events
                            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                        <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                            <Link
                                to="/events/recent"
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={closeMenu}
                            >
                                Recent Events
                            </Link>
                            <Link
                                to="/events/upcoming"
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={closeMenu}
                            >
                                Upcoming Events
                            </Link>
                        </div>
                    </div>

                    <Link
                        to="/blog"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        Blog
                    </Link>
                    <Link
                        to="/contact"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        Contact
                    </Link>
                </nav>

                <div className="hidden md:flex items-center space-x-3 lg:space-x-4">
                    {/* Volunteer Button */}
                    <Link
                        to="/volunteer"
                        className="text-white hover:text-secondary-300 transition-colors font-medium text-xs lg:text-sm uppercase tracking-wide"
                        onClick={closeMenu}
                    >
                        Volunteer
                    </Link>

                    {/* Donate Button */}
                    <Link
                        to="/donation"
                        onClick={closeMenu}
                        className="relative group bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-light hover:to-secondary text-white px-4 lg:px-5 py-1.5 rounded-full font-bold transition-all shadow-premium hover:shadow-premium-lg transform hover:scale-105 inline-block overflow-hidden text-xs lg:text-sm"
                    >
                        <span className="relative z-10">Donate Now</span>
                        <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                    </Link>
                </div>

            </div>

            {/* Mobile Menu Overlay - Only shown on mobile */}
            <div
                className={`md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={closeMenu}
            ></div>

            {/* Mobile Menu - Only shown on mobile */}
            <div
                ref={menuRef}
                className={`md:hidden fixed top-0 right-0 h-full w-4/5 max-w-sm bg-gradient-to-b from-primary to-primary-dark shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Close Button */}
                <button
                    onClick={closeMenu}
                    className="absolute top-4 right-4 text-white text-2xl focus:outline-none p-2"
                    aria-label="Close menu"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="flex flex-col h-full pt-20 px-8 overflow-y-auto">
                    <nav className="flex flex-col space-y-4">
                        <Link
                            to="/"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.1s' }}
                            onClick={closeMenu}
                        >
                            Home
                        </Link>
                        <Link
                            to="/about"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.2s' }}
                            onClick={closeMenu}
                        >
                            About Us
                        </Link>
                        <Link
                            to="/thematic-areas"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.3s' }}
                            onClick={closeMenu}
                        >
                            Thematic Areas
                        </Link>
                        {/* Mobile Events with Submenu */}
                        <div
                            className={`text-white font-medium text-lg border-b border-white/10 pb-2 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.4s' }}
                        >
                            <div className="flex justify-between items-center mb-2">
                                <span>Events</span>
                            </div>
                            <div className="pl-4 space-y-2 text-base">
                                <Link to="/events/recent" className="block opacity-80 hover:opacity-100" onClick={closeMenu}>Recent Events</Link>
                                <Link to="/events/upcoming" className="block opacity-80 hover:opacity-100" onClick={closeMenu}>Upcoming Events</Link>
                            </div>
                        </div>

                        <Link
                            to="/blog"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.5s' }}
                            onClick={closeMenu}
                        >
                            Blog
                        </Link>
                        <Link
                            to="/contact"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.6s' }}
                            onClick={closeMenu}
                        >
                            Contact
                        </Link>
                        <Link
                            to="/volunteer"
                            className={`block text-white font-medium text-lg border-b border-white/10 pb-2 hover:text-secondary-300 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`}
                            style={{ animationDelay: '0.7s' }}
                            onClick={closeMenu}
                        >
                            Volunteer
                        </Link>
                    </nav>

                    <div className={`mt-8 ${isMenuOpen ? 'animate-slide-in-right opacity-0' : ''}`} style={{ animationDelay: '0.8s' }}>
                        <Link
                            to="/donation"
                            className="block w-full text-center bg-secondary hover:bg-secondary-dark text-white font-bold py-4 px-6 rounded-full shadow-lg transition-transform active:scale-95 text-lg"
                            onClick={closeMenu}
                        >
                            Donate Now
                        </Link>
                    </div>

                    <div className="mt-auto py-10 text-white/60 text-sm text-center">
                        <p>© {new Date().getFullYear()} LV Trust</p>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
